import { useState, useEffect } from 'react';
import { Workspace } from '../types/schema';
import { useWorkspace } from '../contexts/workspace-context';
import { workspaceService } from '../services/workspace-service';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Loader2 } from 'lucide-react';

interface WorkspaceSettingsModalProps {
    workspace: Workspace;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function WorkspaceSettingsModal({
    workspace,
    open,
    onOpenChange,
}: WorkspaceSettingsModalProps) {
    const { refetchWorkspaces } = useWorkspace();
    const [name, setName] = useState(workspace.name);
    const [description, setDescription] = useState(workspace.description || '');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Reset form when modal opens or workspace changes
    useEffect(() => {
        if (open) {
            setName(workspace.name);
            setDescription(workspace.description || '');
            setError(null);
        }
    }, [open, workspace]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!name.trim()) {
            setError('Workspace name is required');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            await workspaceService.updateWorkspace(workspace.id, {
                name: name.trim(),
                description: description.trim() || undefined,
            });

            await refetchWorkspaces();
            onOpenChange(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update workspace');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setName(workspace.name);
        setDescription(workspace.description || '');
        setError(null);
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Workspace Settings</DialogTitle>
                    <DialogDescription>
                        Update your workspace details. These changes will be visible to all workspace members.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <label htmlFor="workspace-name" className="text-sm font-medium text-foreground">
                            Workspace name
                        </label>
                        <Input
                            id="workspace-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Enter workspace name"
                            disabled={isLoading}
                            autoFocus
                        />
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="workspace-description" className="text-sm font-medium text-foreground">
                            Description
                            <span className="text-muted-foreground font-normal ml-1">(optional)</span>
                        </label>
                        <Input
                            id="workspace-description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="What is this workspace for?"
                            disabled={isLoading}
                        />
                    </div>

                    {error && (
                        <div className="text-sm text-danger bg-danger/10 px-3 py-2 rounded-component">
                            {error}
                        </div>
                    )}

                    <DialogFooter className="gap-2 sm:gap-2">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={handleCancel}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isLoading || !name.trim()}>
                            {isLoading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                'Save changes'
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
